import styled, { css, CSSProperties } from 'styled-components';
import { Box, Checkbox, CheckboxProps } from '../atoms';
import { colors } from '../utils/colors';
import {
    ButtonProps,
    commonButtonStyles,
    defaultButtonStyle,
    standardButtonStylesAcrossAllCompanies,
} from './Button';

type Props = {
    className?: string;
    isError?: boolean;
    isDisabled?: boolean;
    children: string | React.ReactNode;
    isSelected: boolean;
    button?: Omit<ButtonProps, 'children' | 'onClick' | 'variant'>;
    checkbox?: Omit<CheckboxProps, 'onChange' | 'isSelected'>;
    onChange: (selected: boolean) => void;
    ariaLabel?: string;
};

type CheckboxButtonVariant = keyof typeof defaultToggleButtonStyle;

export const CheckboxButtonContainer = styled.button<{
    $variant: CheckboxButtonVariant;
    disabled?: boolean;
    $isSelected?: boolean;
    $isError?: boolean;
}>`
    ${(props) => props.theme.toggleButton[props.$variant]}
    ${standardButtonStylesAcrossAllCompanies}

    ${(props) => {
        const variantStyles = props.theme.toggleButton[props.$variant];

        if (props.disabled) {
            return css`
                background: ${variantStyles.disabled.background};
                color: ${variantStyles.disabled.color};
                outline: ${variantStyles.disabled.color};
                cursor: not-allowed;
                // when a button is disabled, its automatically added some border, so have to set it to none other wise the button changes in size
                // We see this for the dnb style
                outline: ${variantStyles.disabled.outline || 'none'};
            `;
        }

        if (props.$isSelected) {
            return css`
                ${variantStyles.default};

                &:focus-visible {
                    ${variantStyles.focus};
                }

                &:hover {
                    ${variantStyles.hover};
                }

                &:active {
                    ${variantStyles.active};
                }
            `;
        }

        if (props.$isError) {
            return css`
                background: ${variantStyles.error.background};
                color: ${variantStyles.error.color};
            `;
        }
        return css`
            ${variantStyles.default};

            &:focus-visible {
                ${variantStyles.focus};
            }
            &:hover {
                ${variantStyles.hover};
            }
            &:active {
                ${variantStyles.active};
            }
        `;
    }}
`;

export function CheckboxButton(props: Props) {
    return (
        <CheckboxButtonContainer
            role="checkbox"
            className={props.className}
            $variant={props.isSelected ? 'selected' : 'unselected'}
            disabled={props.isDisabled}
            $isSelected={props.isSelected}
            $isError={props.isError}
            aria-checked={props.isSelected}
            aria-label={props.ariaLabel}
            onClick={() => {
                props.onChange(!props.isSelected);
            }}
            {...props.button}
        >
            <Box gap="12" alignItems="center" wrap="nowrap" aria-hidden>
                <Checkbox
                    {...props.checkbox}
                    isButton
                    excludeFromTabOrder
                    isError={props.isError}
                    isSelected={props.isSelected}
                    onChange={() => {
                        props.onChange(!props.isSelected);
                    }}
                />
                {props.children}
            </Box>
        </CheckboxButtonContainer>
    );
}

type ToggleButtonStyle = {
    default: CSSProperties;
    hover: CSSProperties;
    active: CSSProperties;
    focus: CSSProperties;
    disabled: CSSProperties;
    error: CSSProperties;
};

export const defaultToggleButtonStyle: Record<
    'selected' | 'unselected',
    ToggleButtonStyle
> = {
    selected: {
        ...commonButtonStyles,
        ...defaultButtonStyle.tertiary,
        default: {
            background: colors.blue900,
            color: colors.white,
            outline: 'none',
        },

        disabled: {
            background: colors.gray50,
            color: colors.greyscaleTextDisabled,
        },
        error: {
            background: colors.red50,
            color: colors.red700,
        },
    },
    unselected: {
        ...defaultButtonStyle.tertiary,
        error: {
            background: colors.red50,
            color: colors.red700,
        },
    },
};
